import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MetabaseReportApi } from './api';
import type { LinkCustomMetabaseReportRequest } from './types';

const QUERY_KEYS = {
  all: ['metabase-reports'] as const,
  list: (orgId: string) => [...QUERY_KEYS.all, orgId] as const,
};

export const useMetabaseReports = (orgId: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.list(orgId),
    queryFn: () => MetabaseReportApi.list(orgId),
    enabled: !!orgId,
  });
};

export const useRefreshMetabaseReports = (orgId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (keys: string[]) => MetabaseReportApi.refresh(orgId, keys),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.list(orgId) }),
  });
};

export const useLinkCustomMetabaseReport = (orgId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: LinkCustomMetabaseReportRequest) => MetabaseReportApi.linkCustom(orgId, request),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.list(orgId) }),
  });
};

export const useUnlinkCustomMetabaseReport = (orgId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => MetabaseReportApi.unlinkCustom(orgId, key),
    // A failure to turn off the public link comes after the rows are gone, so refetch either way.
    onSettled: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.list(orgId) }),
  });
};
